import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import Loader from "../../../../Common/Loader/loader";
import "./subsBanner.css";
import DocumentModal from "../../../../Common/DocumentModal/DocumentModal";
import { API_BASE_URL } from "../../../../config/api";
import { trackEvent } from "../../../../config/analytics";

type SubscribeFormValues = {
  firstName: string;
  lastName: string;
  workEmail: string;
  industry: string;
  otherIndustry: string;
  company: string;
  designation: string;
  consent: boolean;
};

type IndustryOption = {
  id: number;
  name: string;
};

type SignupPayload = {
  firstName: string;
  lastName: string;
  workEmail: string;
  industry: any;
  company: string;
  designation: string;
  otherIndustry?: string;
};

type ValidationMessage = {
  label1: string;
  label2: string;
};

type SignupApiInfo = Partial<
  Record<
    keyof SubscribeFormValues | "ratelimit",
    ValidationMessage
  >
>;

// type SignupApiInfo = Partial<
//   Record<
//     keyof SubscribeFormValues,
//     ValidationMessage
//   >
// >;

type SignupApiResponse = {
  success: boolean;
  code: string;
  message?: string | SignupApiInfo;
};

const isSignupApiInfo = (
  message: SignupApiResponse["message"]
): message is SignupApiInfo =>
  typeof message === "object" && message !== null && !Array.isArray(message);

const SIGNUP_API_URL = `${API_BASE_URL}/signup`;
const INDUSTRIES_API_URL = `${API_BASE_URL}/industries`;
const INDUSTRY_PAGE_SIZE = 10;

const OTHER_INDUSTRY_ID = "43";

const CAROUSEL_IMAGES = [
  "./image-20260721-070436.png",
  "./image-20260721-070557.png",
  "./image-20260721-070649.png",
];

const validationSchema = Yup.object({
  firstName: Yup.string().trim().required("First name is required"),
  lastName: Yup.string().trim().required("Last name is required"),
  workEmail: Yup.string()
    .trim()
    .email("Please enter a valid email address")
    .required("Work email is required"),
  industry: Yup.string().required("Industry is required"),
 otherIndustry: Yup.string().when("industry", {
  is: (industryId: string) => industryId === OTHER_INDUSTRY_ID,
  then: (schema) =>
    schema
      .trim()
      .max(250, "Other industry cannot exceed 250 characters")
      .required("Other industry is required"),
  otherwise: (schema) => schema.strip(),
}),
  company: Yup.string().trim().required("Company is required"),
  designation: Yup.string().trim().required("Designation is required"),
  consent: Yup.boolean().oneOf([true], "Please accept the terms"),
});

const SubsCribeBanner = () => {
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [apiError, setApiError] = useState("");
  const [industries, setIndustries] = useState<IndustryOption[]>([]);
  const [industryLoading, setIndustryLoading] = useState(false);
  const [industryError, setIndustryError] = useState("");
  const [industrySearch, setIndustrySearch] = useState("");
  const [industryPage, setIndustryPage] = useState(0);
  const [hasMoreIndustries, setHasMoreIndustries] = useState(true);
  const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);

  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const industryDropdownRef = useRef<HTMLDivElement | null>(null);
  const industryRequestId = useRef(0);
  const industryLoadingRef = useRef(false);

  const showPreviousSlide = () => {
    setActiveSlide((currentSlide) =>
      (currentSlide - 1 + CAROUSEL_IMAGES.length) % CAROUSEL_IMAGES.length
    );
  };

  const showNextSlide = () => {
    setActiveSlide(
      (currentSlide) => (currentSlide + 1) % CAROUSEL_IMAGES.length
    );
  };

  const formik = useFormik<SubscribeFormValues>({
    initialValues: {
      firstName: "",
      lastName: "",
      workEmail: "",
      industry: "",
      otherIndustry: "",
      company: "",
      designation: "",
      consent: false,
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, { setSubmitting, setErrors, setTouched }) => {
      try {
        setApiError("");

        const payload: SignupPayload = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          workEmail: values.workEmail.trim(),
          // industry: Number(values.industry), 
          industry: {
            id: Number(values.industry),
            text: isOtherIndustrySelected
              ? values.otherIndustry.trim()
              : "",
          },
          company: values.company.trim(),
          designation: values.designation.trim(),
        };

       
        trackEvent("signup_submit_clicked");
        const response = await axios.post<SignupApiResponse>(
          SIGNUP_API_URL,
          payload
        );

        if (response?.data?.code === "200") {
          setShowVerificationPopup(true);
          trackEvent("verification_email_sent");
          return;
        }

        const message = response?.data?.message;
        // setApiError(
        //   typeof message === "string" ? message : "Something went wrong"
        // );

        if (isSignupApiInfo(message)) {

          
          
          setErrors({
            firstName: message.firstName?.label2,
            lastName: message.lastName?.label2,
            workEmail: message.workEmail?.label2,
            industry: message.industry?.label2,
            otherIndustry: message.otherIndustry?.label2,
            company: message.company?.label2,
            designation: message.designation?.label2,
          });

          setTouched({
            firstName: true,
            lastName: true,
            workEmail: true,
            industry: true,
            otherIndustry: true,
            company: true,
            designation: true,
          }, false);
        }
      } catch (error) {
        if (axios.isAxiosError<SignupApiResponse>(error)) {
          const errorData = error.response?.data;
          const message = errorData?.message;

          // setApiError(
          //   typeof message === "string" ? message : "Something went wrong"
          // );

          if (isSignupApiInfo(message)) {
            if (message.ratelimit) {
            setApiError(message.ratelimit.label2);
          }
            setErrors({
              firstName: message.firstName?.label2,
              lastName: message.lastName?.label2,
              workEmail: message.workEmail?.label2,
              industry: message.industry?.label2,
              otherIndustry: message.otherIndustry?.label2,
              company: message.company?.label2,
              designation: message.designation?.label2,
            });

            setTouched({
              firstName: true,
              lastName: true,
              workEmail: true,
              industry: true,
              otherIndustry: true,
              company: true,
              designation: true,
            }, false);
          }
        } else {
          // setApiError("Something went wrong");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const selectedIndustry = useMemo(
    () =>
      industries.find(
        (industry) => String(industry.id) === formik.values.industry
      ),
    [formik.values.industry, industries]
  );

const isOtherIndustrySelected =
  selectedIndustry?.name.trim().toLowerCase() === "other" ||
  selectedIndustry?.name.trim().toLowerCase() === "others";

  const extractIndustryOptions = (data: unknown): IndustryOption[] => {
    if (Array.isArray(data)) return data as IndustryOption[];

    if (data && typeof data === "object") {
      const responseData = data as Record<string, unknown>;
      const possibleLists = [
        responseData.data,
        responseData.content,
        responseData.records,
        responseData.items,
        responseData.list,
      ];

      const nestedData =
        responseData.data && typeof responseData.data === "object"
          ? (responseData.data as Record<string, unknown>)
          : null;

      if (nestedData) {
        possibleLists.push(
          nestedData.data,
          nestedData.content,
          nestedData.records,
          nestedData.items,
          nestedData.list
        );
      }

      const list = possibleLists.find(Array.isArray);
      if (Array.isArray(list)) return list as IndustryOption[];
    }

    return [];
  };

  const fetchIndustries = useCallback(
    async ({ page, search, append }: { page: number; search: string; append: boolean }) => {
      if (industryLoadingRef.current) return;

      try {
        const requestId = industryRequestId.current + 1;
        industryRequestId.current = requestId;
        industryLoadingRef.current = true;
        setIndustryLoading(true);
        setIndustryError("");

        const response = await axios.post(INDUSTRIES_API_URL, {
          defaultValues: null,
          pageSize: INDUSTRY_PAGE_SIZE,
          pageCur: page,
          search,
        });

        if (requestId !== industryRequestId.current) return;

        const apiIndustries = extractIndustryOptions(response.data);

        const nextIndustries: IndustryOption[] = apiIndustries.map(
          (item: any) => ({
            id: item.value,
            name: item.label?.label1 ?? "",
          })
        );
        
        setIndustries((prevIndustries) => {
          const mergedIndustries = append
            ? [...prevIndustries, ...nextIndustries]
            : nextIndustries;
          const industryMap = new Map<string, IndustryOption>();

          mergedIndustries.forEach((industry) => {
            industryMap.set(String(industry.id), industry);
          });

          return Array.from(industryMap.values());
        });
        setIndustryPage(page);
        setHasMoreIndustries(nextIndustries.length === INDUSTRY_PAGE_SIZE);
      } catch (error) {
        console.error("Error fetching industries:", error);
        setIndustryError("Unable to load industries");
      } finally {
        industryLoadingRef.current = false;
        setIndustryLoading(false);
      }
    },
    []
  );

  // useEffect(() => {
  //   if (!showVerificationPopup || resendTimer === 0) return;

  //   const timer = window.setTimeout(() => {
  //     setResendTimer((prev) => prev - 1);
  //   }, 1000);

  //   return () => window.clearTimeout(timer);
  // }, [showVerificationPopup]);

  const handleGoBackAndEdit = () => {
    setShowVerificationPopup(false);
  };

  useEffect(() => {
    if (isOtherIndustrySelected) return;

    formik.setFieldValue("otherIndustry", "", false);
    formik.setFieldTouched("otherIndustry", false, false);
  }, [isOtherIndustrySelected]);

  useEffect(() => {
    const searchTimer = window.setTimeout(() => {
      fetchIndustries({ page: 0, search: industrySearch.trim(), append: false });
    }, 300);

    return () => window.clearTimeout(searchTimer);
  }, [fetchIndustries, industrySearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        industryDropdownRef.current &&
        !industryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsIndustryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isCarouselPaused) return;

    const carouselTimer = window.setInterval(showNextSlide, 10000);
    return () => window.clearInterval(carouselTimer);
  }, [isCarouselPaused]);

  const handleIndustryScroll = (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => {
    const target = event.currentTarget;
    const isNearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 12;

    if (isNearBottom && hasMoreIndustries && !industryLoading) {
      fetchIndustries({
        page: industryPage + 1,
        search: industrySearch.trim(),
        append: true,
      });
    }
  };

  const handleIndustrySelect = (industry: IndustryOption) => {
  // formik.setFieldValue("industry", String(industry.id));
  formik.setFieldValue("industry", String(industry.id), true);
  formik.setFieldTouched("industry", true, false);
  setIsIndustryDropdownOpen(false);
};

  // const handlePrivacyRedirect = () => {
  //   window.open(
  //     "https://www.gdprprivacynotice.com/live.php?token=MHpZ9vGVTXG3XyQwa366HuABZevqzcKE",
  //     "_blank",
  //     "noopener,noreferrer"
  //   );
  // };



  return (
    <section className="regpulse-section">
      <div className="regpulse-layout">
      <div className="regpulse-content">
        {/* <p className="regpulse-eyebrow">EVONEXT REGPULSE</p> */}

        <h2 className="regpulse-title">EvoNext RegPulse Email Digest</h2>

        <p className="regpulse-description">
          Stay ahead of regulatory change without reading every circular yourself. Subscribe for AI-curated updates on regulatory changes*, delivered to your inbox — with plain-language summaries of what changed, why it matters, and what it means for your organization.
        </p>
      <div  className = "maincontainer_form_n_image">
        <div>
             <form className="regpulse-form" onSubmit={formik.handleSubmit}>
          <div className="regpulse-form-grid">
            <div className="regpulse-field">
              <label htmlFor="firstName">
                First name <span>*</span>
              </label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                placeholder="Sarah"
                value={formik.values.firstName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^a-zA-Z\s'-]/g, "") // Allow only letters, spaces, ' and -
                    .slice(0, 250); // Limit to 250 characters

                  formik.setFieldValue("firstName", value);
                }}
                onBlur={formik.handleBlur}
                maxLength={250}
                className={
                  formik.touched.firstName && formik.errors.firstName
                    ? "regpulse-input-error"
                    : ""
                }
              />
              {formik.touched.firstName && formik.errors.firstName && (
                <p className="regpulse-error">{formik.errors.firstName}</p>
              )}
            </div>

            <div className="regpulse-field">
              <label htmlFor="lastName">
                Last name <span>*</span>
              </label>
              <input
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Mitchell"
                value={formik.values.lastName}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^a-zA-Z\s'-]/g, "") // Allow only letters, spaces, ' and -
                    .slice(0, 250); // Limit to 250 characters

                  formik.setFieldValue("lastName", value);
                }}
                onBlur={formik.handleBlur}
                maxLength={250}
                className={
                  formik.touched.lastName && formik.errors.lastName
                    ? "regpulse-input-error"
                    : ""
                }
              />
              {formik.touched.lastName && formik.errors.lastName && (
                <p className="regpulse-error">{formik.errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="regpulse-field">
            <label htmlFor="workEmail">
              Work email <span>*</span>
            </label>
            <input
              id="workEmail"
              name="workEmail"
              type="email"
              placeholder="xyz@companymail.com"
              value={formik.values.workEmail}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={
                formik.touched.workEmail && formik.errors.workEmail
                  ? "regpulse-input-error"
                  : ""
              }
            />
            {formik.touched.workEmail && formik.errors.workEmail && (
              <p className="regpulse-error">{formik.errors.workEmail}</p>
            )}
          </div>

          <div className="regpulse-form-grid">
            <div className="regpulse-field">
              <label htmlFor="industry">
                Industry <span>*</span>
              </label>

              <div
                className="regpulse-industry-dropdown"
                ref={industryDropdownRef}
              >
                <button
                  id="industry"
                  type="button"
                  className={`regpulse-industry-trigger ${
                    formik.touched.industry && formik.errors.industry
                      ? "regpulse-input-error"
                      : ""
                  }`}
                  onClick={() =>
                    setIsIndustryDropdownOpen((isOpen) => !isOpen)
                  }
                  onBlur={() => formik.setFieldTouched("industry", true)}
                >
                  <span>
                    {isOtherIndustrySelected
                      ? "Other"
                      : selectedIndustry?.name || "Select industry"}
                  </span>
                  <span className="regpulse-industry-caret">v</span>
                </button>

                {isIndustryDropdownOpen && (
                  <div className="regpulse-industry-menu">
                    <input
                      type="text"
                      value={industrySearch}
                      onChange={(event) =>
                        setIndustrySearch(event.target.value)
                      }
                      placeholder="Search industry"
                      className="regpulse-industry-search"
                    />

                    <div
                      className="regpulse-industry-options"
                      onScroll={handleIndustryScroll}
                    >
                      {industries.map((industry) => (
                        <button
                          key={industry.id}
                          type="button"
                          className="regpulse-industry-option"
                          onClick={() => handleIndustrySelect(industry)}
                        >
                          {industry.name}
                        </button>
                      ))}

                      {industryLoading && (
                        <div className="regpulse-industry-status">
                          Loading industries...
                        </div>
                      )}

                      {!industryLoading && industries.length === 0 && (
                        <div className="regpulse-industry-status">
                          No industries found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {industryError && (
                <p className="regpulse-error">{industryError}</p>
              )}

              {formik.touched.industry && formik.errors.industry && (
                <p className="regpulse-error">{formik.errors.industry}</p>
              )}
            </div>

            {isOtherIndustrySelected && (
              <div className="regpulse-field">
                <label htmlFor="otherIndustry">
                  Other industry <span>*</span>
                </label>
                <input
                  id="otherIndustry"
                  name="otherIndustry"
                  type="text"
                  placeholder="Enter your industry"
                  value={formik.values.otherIndustry}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, 250);
                    formik.setFieldValue("otherIndustry", value);
                  }}
                  onBlur={formik.handleBlur}
                  maxLength={250}
                  className={
                    formik.touched.otherIndustry &&
                    formik.errors.otherIndustry
                      ? "regpulse-input-error"
                      : ""
                  }
                />
                {formik.touched.otherIndustry &&
                  formik.errors.otherIndustry && (
                    <p className="regpulse-error">
                      {formik.errors.otherIndustry}
                    </p>
                  )}
              </div>
            )}

            <div className="regpulse-field">
              <label htmlFor="company">
                Company <span>*</span>
              </label>
              <input
                id="company"
                name="company"
                type="text"
                placeholder="Your company"
                value={formik.values.company}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, "").slice(0, 250);
                  formik.setFieldValue("company", value);
                }}
                onBlur={formik.handleBlur}
                className={
                  formik.touched.company && formik.errors.company
                    ? "regpulse-input-error"
                    : ""
                }
              />
              {formik.touched.company && formik.errors.company && (
                <p className="regpulse-error">{formik.errors.company}</p>
              )}
            </div>
          </div>

          <div className="regpulse-field">
            <label htmlFor="designation">
              Designation <span>*</span>
            </label>
            <input
              id="designation"
              name="designation"
              type="text"
              placeholder="e.g. Chief Risk Officer"
              value={formik.values.designation}
              onChange={(e) => {
                const value = e.target.value
                  .replace(/[^a-zA-Z0-9\s&.'-]/g, "") // Allow only valid characters
                  .slice(0, 250); // Limit to 250 characters

                formik.setFieldValue("designation", value);
              }}
              onBlur={formik.handleBlur}
              maxLength={250}
              className={
                formik.touched.designation && formik.errors.designation
                  ? "regpulse-input-error"
                  : ""
              }
            />
            {formik.touched.designation && formik.errors.designation && (
              <p className="regpulse-error">{formik.errors.designation}</p>
            )}
          </div>

          <div className="regpulse-consent">
            <div style={{paddingLeft: "27px" , marginBottom:"10px"}}>*Currently covering RBI, with a roadmap to expand to other regulators.</div>
            <div  style={{paddingLeft: "27px" ,  marginBottom:"10px"}}>Free while in beta. We'll introduce pricing as RegPulse matures, and beta subscribers will get advance notice before anything changes. Unsubscribe anytime.</div>
            <div className= "regpulse-consent-checkbox">
              <input
              id="consent"
              type="checkbox"
              name="consent"
              checked={formik.values.consent}
              onChange={formik.handleChange}
            />

            <div className="regpulse-consent-text">
              By signing up, I agree to the{" "}
              {/* <span
                className="regpulse-consent-link"
                onClick={handlePrivacyRedirect}
              >
                RegPulse Privacy Statement{" "}
              </span> */}
                      <a
                        href={`${process.env.PUBLIC_URL}/PrivacyPolicy.pdf`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="regpulse-consent-link"
                      >
                         Privacy Policy{" "}
                      </a>
              and {" "}
              {/* <span
                className="regpulse-consent-link"
                onClick={() => setShowTerms(true)}
              >
                  Terms of Use{" "}
                </span> */}
                {/* <Link
                  to="/regpulsetermsofuse"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="regpulse-consent-link"
                >
                  Terms of Use{" "}
                </Link> */}
                          <a
                          
                            href={`${process.env.PUBLIC_URL}/TermsOfUse.pdf`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="regpulse-consent-link"
                          >
                            Terms of Use{" "}
                          </a>
              and consent to receive the EvoNext RegPulse Email Digest.
            </div>
            </div>
          </div>


          {apiError && <p className="regpulse-api-error">{apiError}</p>}

          <button
            type="submit"
            className="regpulse-submit"
            disabled={formik.isSubmitting
              || !formik.isValid
              || !formik.values.consent}
          >
            {formik.isSubmitting ? <Loader /> : "Subscribe"}
          </button>
        </form>
        </div>
        <div className = "carousel_container_main">
               <aside
        className="regpulse-carousel"
        aria-label="RegPulse highlights"
        onMouseEnter={() => setIsCarouselPaused(true)}
        onMouseLeave={() => setIsCarouselPaused(false)}
        onFocus={() => setIsCarouselPaused(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node)) {
            setIsCarouselPaused(false);
          }
        }}
      >
        <div className="regpulse-carousel-frame">
          {CAROUSEL_IMAGES.map((image, index) => (
            <img
              key={image}
              className={`regpulse-carousel-image ${
                index === activeSlide ? "is-active" : ""
              }`}
              src={image}
              alt={`RegPulse highlight ${index + 1}`}
              aria-hidden={index !== activeSlide}
            />
          ))}

          <button
            type="button"
            className="regpulse-carousel-control regpulse-carousel-previous"
            onClick={(event) => {
              showPreviousSlide();
              event.currentTarget.blur();
            }}
            aria-label="Show previous slide"
          >
            &#8249;
          </button>
          <button
            type="button"
            className="regpulse-carousel-control regpulse-carousel-next"
            onClick={(event) => {
              showNextSlide();
              event.currentTarget.blur();
            }}
            aria-label="Show next slide"
          >
            &#8250;
          </button>
        </div>

        <div
          className="regpulse-carousel-indicators"
          aria-label={`Showing slide ${activeSlide + 1} of ${CAROUSEL_IMAGES.length}`}
        >
          {CAROUSEL_IMAGES.map((image, index) => (
            <span
              key={image}
              className={`regpulse-carousel-indicator ${
                index === activeSlide ? "is-active" : ""
              }`}
              aria-hidden="true"
            />
          ))}
        </div>
      </aside>

        </div>
      </div>
       

      </div>

     
      </div>

      {/* <div className="bottomTerms">
        <div>*currently RBI, however may include other regulatory bodies as the service evolves in future</div>
      <div>**currently free during the beta phase, however it may change as the service evolves </div>
      </div> */}

      {showVerificationPopup && (
        <div className="verification-popup-overlay">
          <div className="verification-popup">
            <div className="verification-popup-header">
              <div className="verification-mail-icon">@</div>

              <div>
                <h3>Check your inbox</h3>
                <p>Verification email sent</p>
              </div>

              <button
                type="button"
                className="verification-close"
                onClick={handleGoBackAndEdit}
                aria-label="Close popup"
              >
                x
              </button>
            </div>

            <div className="verification-popup-body">
              <p className="verification-copy">
                We've sent a verification link to
              </p>

              <div className="verification-email-box">
                <span>@</span>
                <strong className="popup_email">{formik.values.workEmail}</strong>
              </div>

              <div className="verification-warning">
                <span>i</span>
                <p>
                  The link expires in <strong>24 hours.</strong> If you don't
                  verify in time, you'll need to sign up again.
                </p>
              </div>

              {/* <p className="verification-muted">didn't receive it?</p> */}

              {/* <button
                type="button"
                className="verification-resend"
                disabled={resendTimer > 0}
                onClick={handleResendVerification}
              >
                {resendTimer > 0
                  ? `Resend verification email in ${resendTimer}s`
                  : "Resend verification email"}
              </button> */}

              <p className="verification-edit-text">
                Wrong email?{" "}
                <button type="button" onClick={handleGoBackAndEdit}>
                  Go back and edit
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      <DocumentModal
        open={showPrivacy}
        title="RegPulse Privacy Statement"
        docUrl="/evonext.ai/RegPulse-Privacy-Policy-v2.docx"
        onClose={() => setShowPrivacy(false)}
      />

      <DocumentModal
        open={showTerms}
        title="Terms of Use"
        docUrl="/evonext.ai/RegPulse-Terms-of-Use-v2.docx"
        onClose={() => setShowTerms(false)}
      />

    </section>
  );
};

export default SubsCribeBanner;
